import { useEffect, useState } from "react";
import {
  X,
  Pin,
  PinOff,
  Trash2,
  UserPlus,
  ArrowRight,
  Loader2,
  Archive,
  CalendarClock,
  TagIcon,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import type { MappedConversation } from "@/lib/conversation-list-map";
import type { LeadTag, SalesFunnel } from "@/types/lead";
import { resolveFunnelDisplayName } from "@/lib/funnel-display-label";
import { bulkSetPinnedConversations } from "@/lib/conversation-pin-storage";
import {
  bulkCloseConversations,
  bulkDeleteConversations,
  bulkAssignLeadsToMember,
  bulkMoveLeadsToStage,
  bulkAddTagToLeads,
  bulkRemoveTagFromLeads,
  uniqueLeadIdsFromConversations,
  summarizeBulkResult,
  bulkProgressPercent,
  type BulkOperationProgress,
} from "@/lib/bulk-conversation-operations";
import { BulkScheduleCloseDialog } from "@/components/conversations/BulkScheduleCloseDialog";
import { cn } from "@/lib/utils";

interface Stage {
  id: string;
  name: string;
  slug: string;
  color: string;
}

interface WorkspaceMember {
  id: string;
  display_name?: string | null;
  profile?: { full_name: string | null } | null;
}

interface ConversationBulkActionsBarProps {
  workspaceId: string;
  selectedConversations: MappedConversation[];
  pinnedConversationIds: Set<string>;
  onPinnedChange: (ids: Set<string>) => void;
  memberId?: string | null;
  memberDisplayName?: string | null;
  visibleSelectableCount?: number;
  allVisibleSelected?: boolean;
  onSelectAllVisible?: (conversationIds: string[]) => void;
  visibleSelectableIds?: string[];
  workspaceTags?: LeadTag[];
  onComplete: () => void;
  onCancel: () => void;
}

export function ConversationBulkActionsBar({
  workspaceId,
  selectedConversations,
  pinnedConversationIds,
  onPinnedChange,
  memberId,
  memberDisplayName,
  visibleSelectableCount = 0,
  allVisibleSelected = false,
  onSelectAllVisible,
  visibleSelectableIds = [],
  workspaceTags = [],
  onComplete,
  onCancel,
}: ConversationBulkActionsBarProps) {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState<BulkOperationProgress | null>(null);
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
  const [moveOpen, setMoveOpen] = useState(false);
  const [addTagOpen, setAddTagOpen] = useState(false);
  const [removeTagOpen, setRemoveTagOpen] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);
  const [funnels, setFunnels] = useState<SalesFunnel[]>([]);
  const [selectedFunnelId, setSelectedFunnelId] = useState<string | null>(null);
  const [funnelStages, setFunnelStages] = useState<Stage[]>([]);
  const [loadingStages, setLoadingStages] = useState(false);
  const [members, setMembers] = useState<WorkspaceMember[]>([]);
  const [loadingMembers, setLoadingMembers] = useState(false);

  const selectedCount = selectedConversations.length;
  const conversationIds = selectedConversations.map((c) => c.conversation_id);
  const leadIds = uniqueLeadIdsFromConversations(selectedConversations);

  useEffect(() => {
    if (!moveOpen || funnels.length > 0) return;
    void supabase
      .from("sales_funnels")
      .select("id, name, order_index, is_default, integration_id")
      .eq("workspace_id", workspaceId)
      .order("order_index", { ascending: true })
      .then(({ data }) => setFunnels((data as SalesFunnel[]) || []));
  }, [moveOpen, workspaceId, funnels.length]);

  useEffect(() => {
    if (!assignOpen || members.length > 0) return;
    setLoadingMembers(true);
    void supabase
      .from("workspace_members")
      .select("id, display_name, profile:profiles(full_name)")
      .eq("workspace_id", workspaceId)
      .then(({ data }) => {
        setMembers((data as unknown as WorkspaceMember[]) || []);
        setLoadingMembers(false);
      });
  }, [assignOpen, workspaceId, members.length]);

  if (selectedCount === 0) return null;

  const handlePin = (pin: boolean) => {
    const next = bulkSetPinnedConversations(
      workspaceId,
      conversationIds,
      pin,
      pinnedConversationIds,
    );
    onPinnedChange(next);
    toast({
      title: pin ? "Conversas fixadas" : "Conversas desafixadas",
      description: `${selectedCount} conversa(s) atualizada(s).`,
    });
    onComplete();
  };

  const runClose = async (preserveAssignees: boolean) => {
    setLoading(true);
    try {
      const result = await bulkCloseConversations(conversationIds, {
        preserveAssignees,
        memberId,
        workspaceId,
        memberDisplayName,
        onProgress: setProgress,
      });
      const summary = summarizeBulkResult(result, "Encerramento");
      toast({
        title: summary.title,
        description: summary.description,
        variant: summary.variant,
      });
      onComplete();
    } catch (err) {
      toast({
        title: "Erro ao encerrar",
        description: err instanceof Error ? err.message : "Erro desconhecido",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
      setProgress(null);
    }
  };

  const handleDelete = async () => {
    setLoading(true);
    try {
      const result = await bulkDeleteConversations(conversationIds, setProgress);
      const summary = summarizeBulkResult(result, "Exclusão");
      toast({
        title: summary.title,
        description: summary.description,
        variant: summary.variant,
      });
      onComplete();
    } catch (err) {
      toast({
        title: "Erro ao excluir",
        description: err instanceof Error ? err.message : "Erro desconhecido",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
      setProgress(null);
    }
  };

  const handleSelectFunnel = async (funnelId: string) => {
    setSelectedFunnelId(funnelId);
    setLoadingStages(true);
    try {
      const { data, error } = await supabase
        .from("lead_stages")
        .select("id, name, slug, color")
        .eq("funnel_id", funnelId)
        .order("order_index", { ascending: true });
      if (error) throw error;
      setFunnelStages(data || []);
    } catch {
      setFunnelStages([]);
    } finally {
      setLoadingStages(false);
    }
  };

  const handleMoveStage = async (stageSlug: string, targetFunnelId: string) => {
    if (leadIds.length === 0) {
      toast({
        title: "Nenhum lead encontrado",
        description: "As conversas selecionadas não possuem lead vinculado.",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    setMoveOpen(false);
    try {
      const sourceFunnelId = selectedConversations[0]?.lead_funnel_id ?? null;
      const result = await bulkMoveLeadsToStage(leadIds, workspaceId, stageSlug, {
        funnelId: targetFunnelId,
        sourceFunnelId,
        targetFunnelId,
        onProgress: setProgress,
      });
      const summary = summarizeBulkResult(result, "Movimentação");
      toast({
        title: summary.title,
        description: summary.description,
        variant: summary.variant,
      });
      onComplete();
    } catch (err) {
      toast({
        title: "Erro ao mover",
        description: err instanceof Error ? err.message : "Erro desconhecido",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
      setProgress(null);
    }
  };

  const handleAddTag = async (tagId: string) => {
    if (leadIds.length === 0) {
      toast({
        title: "Nenhum lead encontrado",
        description: "As conversas selecionadas não possuem lead vinculado.",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    setAddTagOpen(false);
    try {
      const result = await bulkAddTagToLeads(leadIds, tagId, setProgress);
      const summary = summarizeBulkResult(result, "Adição de tag");
      const tagName = workspaceTags.find((t) => t.id === tagId)?.name || "";
      toast({
        title: summary.variant ? summary.title : "Tag adicionada",
        description: summary.variant
          ? summary.description
          : `Tag "${tagName}" adicionada a ${leadIds.length} lead(s).`,
        variant: summary.variant,
      });
      onComplete();
    } catch (err) {
      toast({
        title: "Erro ao adicionar tag",
        description: err instanceof Error ? err.message : "Erro desconhecido",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
      setProgress(null);
    }
  };

  const handleRemoveTag = async (tagId: string) => {
    if (leadIds.length === 0) {
      toast({
        title: "Nenhum lead encontrado",
        description: "As conversas selecionadas não possuem lead vinculado.",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    setRemoveTagOpen(false);
    try {
      const result = await bulkRemoveTagFromLeads(leadIds, tagId, setProgress);
      const summary = summarizeBulkResult(result, "Remoção de tag");
      const tagName = workspaceTags.find((t) => t.id === tagId)?.name || "";
      toast({
        title: summary.variant ? summary.title : "Tag removida",
        description: summary.variant
          ? summary.description
          : `Tag "${tagName}" removida de ${leadIds.length} lead(s).`,
        variant: summary.variant,
      });
      onComplete();
    } catch (err) {
      toast({
        title: "Erro ao remover tag",
        description: err instanceof Error ? err.message : "Erro desconhecido",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
      setProgress(null);
    }
  };

  const handleAssignMember = async (assignMemberId: string) => {
    if (leadIds.length === 0) {
      toast({
        title: "Nenhum lead encontrado",
        description: "As conversas selecionadas não possuem lead vinculado.",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    setAssignOpen(false);
    try {
      const result = await bulkAssignLeadsToMember(leadIds, assignMemberId, {
        workspaceId,
        onProgress: setProgress,
      });
      const summary = summarizeBulkResult(result, "Atribuição");
      toast({
        title: summary.title,
        description: summary.description,
        variant: summary.variant,
      });
      onComplete();
    } catch (err) {
      toast({
        title: "Erro ao atribuir",
        description: err instanceof Error ? err.message : "Erro desconhecido",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
      setProgress(null);
    }
  };

  const memberLabel = (m: WorkspaceMember) =>
    (m.display_name || m.profile?.full_name || "Membro").trim();

  const showProgress = loading && progress && progress.total > 0;

  return (
    <>
      <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 animate-in slide-in-from-bottom-4 fade-in duration-300">
        <div className="flex flex-col gap-2 px-4 py-3 bg-background/95 backdrop-blur-md border rounded-xl shadow-lg min-w-[min(100vw-2rem,640px)] max-w-[min(100vw-2rem,720px)]">
          {showProgress && (
            <div className="space-y-1.5 pb-1 border-b">
              <div className="flex items-center justify-between gap-2">
                <p className="text-xs font-medium text-muted-foreground truncate">
                  {progress.label}
                </p>
                <p className="text-xs text-muted-foreground tabular-nums shrink-0">
                  {progress.completed} / {progress.total}
                </p>
              </div>
              <Progress value={bulkProgressPercent(progress)} className="h-1.5" />
            </div>
          )}

          <div className="flex items-center gap-2 flex-wrap justify-center">
            {loading && (
              <Loader2 className="w-4 h-4 animate-spin text-muted-foreground shrink-0" />
            )}

            <Badge variant="secondary" className="text-sm font-medium shrink-0">
              {selectedCount} selecionada{selectedCount !== 1 ? "s" : ""}
            </Badge>

            {visibleSelectableCount > 0 && onSelectAllVisible && (
              <Button
                variant="ghost"
                size="sm"
                className="h-8 px-2 text-xs text-muted-foreground"
                onClick={() =>
                  onSelectAllVisible(allVisibleSelected ? [] : visibleSelectableIds)
                }
                disabled={loading}
              >
                {allVisibleSelected ? "Limpar" : "Selecionar todas"}
              </Button>
            )}

            <div className="w-px h-6 bg-border mx-0.5 hidden sm:block shrink-0" />

            <Button
              variant="outline"
              size="sm"
              onClick={() => handlePin(true)}
              disabled={loading}
            >
              <Pin className="w-4 h-4" />
              <span className="hidden sm:inline ml-1.5">Fixar</span>
            </Button>

            <Button
              variant="outline"
              size="sm"
              onClick={() => handlePin(false)}
              disabled={loading}
            >
              <PinOff className="w-4 h-4" />
              <span className="hidden sm:inline ml-1.5">Desafixar</span>
            </Button>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" disabled={loading}>
                  <Archive className="w-4 h-4" />
                  <span className="hidden sm:inline ml-1.5">Encerrar</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="center" side="top">
                <DropdownMenuItem onSelect={() => void runClose(false)}>
                  Apenas encerrar
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => void runClose(true)}>
                  Encerrar sem desatribuir
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => setScheduleDialogOpen(true)}>
                  Agendar mensagem...
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <Button
              variant="outline"
              size="sm"
              onClick={() => setScheduleDialogOpen(true)}
              disabled={loading}
            >
              <CalendarClock className="w-4 h-4" />
              <span className="hidden sm:inline ml-1.5">Agendar</span>
            </Button>

            <Popover open={moveOpen} onOpenChange={setMoveOpen}>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" disabled={loading}>
                  <ArrowRight className="w-4 h-4" />
                  <span className="hidden sm:inline ml-1.5">Mover</span>
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-64 p-2" align="center" side="top">
                {!selectedFunnelId ? (
                  <div className="space-y-1 max-h-60 overflow-y-auto">
                    <p className="text-xs font-medium text-muted-foreground px-2 py-1">
                      Escolha o funil
                    </p>
                    {funnels.map((funnel) => (
                      <button
                        key={funnel.id}
                        type="button"
                        className="w-full flex items-center gap-2 px-2 py-1.5 text-sm rounded-md hover:bg-muted text-left"
                        onClick={() => void handleSelectFunnel(funnel.id)}
                      >
                        <span className="truncate">{resolveFunnelDisplayName(funnel)}</span>
                      </button>
                    ))}
                  </div>
                ) : loadingStages ? (
                  <div className="flex justify-center py-4">
                    <Loader2 className="w-4 h-4 animate-spin" />
                  </div>
                ) : (
                  <div className="space-y-1 max-h-60 overflow-y-auto">
                    <button
                      type="button"
                      className="w-full text-left text-xs text-muted-foreground hover:text-foreground px-2 py-1"
                      onClick={() => setSelectedFunnelId(null)}
                    >
                      Voltar aos funis
                    </button>
                    {funnelStages.map((stage) => (
                      <button
                        key={stage.id}
                        type="button"
                        className="w-full flex items-center gap-2 px-2 py-1.5 text-sm rounded-md hover:bg-muted text-left"
                        onClick={() => void handleMoveStage(stage.slug, selectedFunnelId)}
                      >
                        <span
                          className="w-2.5 h-2.5 rounded-full shrink-0"
                          style={{ backgroundColor: stage.color }}
                        />
                        <span className="truncate">{stage.name}</span>
                      </button>
                    ))}
                  </div>
                )}
              </PopoverContent>
            </Popover>

            <Button
              variant="outline"
              size="sm"
              onClick={() => setAssignOpen(true)}
              disabled={loading}
            >
              <UserPlus className="w-4 h-4" />
              <span className="hidden sm:inline ml-1.5">Atribuir</span>
            </Button>

            <Popover open={addTagOpen} onOpenChange={setAddTagOpen}>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" disabled={loading}>
                  <TagIcon className="w-4 h-4" />
                  <span className="hidden sm:inline ml-1.5">+ Tag</span>
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-48 p-2" align="center" side="top">
                <p className="text-xs font-medium text-muted-foreground px-2 pb-2">
                  Adicionar tag
                </p>
                {workspaceTags.length === 0 ? (
                  <p className="text-xs text-muted-foreground text-center py-3">
                    Nenhuma tag criada
                  </p>
                ) : (
                  <div className="space-y-0.5 max-h-60 overflow-y-auto">
                    {workspaceTags.map((tag) => (
                      <button
                        key={tag.id}
                        type="button"
                        className="w-full flex items-center gap-2 px-2 py-1.5 text-sm rounded-md hover:bg-muted text-left"
                        onClick={() => void handleAddTag(tag.id)}
                      >
                        <div className={cn("w-2.5 h-2.5 rounded-full shrink-0", tag.color)} />
                        <span className="truncate">{tag.name}</span>
                      </button>
                    ))}
                  </div>
                )}
              </PopoverContent>
            </Popover>

            <Popover open={removeTagOpen} onOpenChange={setRemoveTagOpen}>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" disabled={loading}>
                  <TagIcon className="w-4 h-4" />
                  <span className="hidden sm:inline ml-1.5">- Tag</span>
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-48 p-2" align="center" side="top">
                <p className="text-xs font-medium text-muted-foreground px-2 pb-2">
                  Remover tag
                </p>
                {workspaceTags.length === 0 ? (
                  <p className="text-xs text-muted-foreground text-center py-3">
                    Nenhuma tag criada
                  </p>
                ) : (
                  <div className="space-y-0.5 max-h-60 overflow-y-auto">
                    {workspaceTags.map((tag) => (
                      <button
                        key={tag.id}
                        type="button"
                        className="w-full flex items-center gap-2 px-2 py-1.5 text-sm rounded-md hover:bg-muted text-left"
                        onClick={() => void handleRemoveTag(tag.id)}
                      >
                        <div className={cn("w-2.5 h-2.5 rounded-full shrink-0", tag.color)} />
                        <span className="truncate">{tag.name}</span>
                      </button>
                    ))}
                  </div>
                )}
              </PopoverContent>
            </Popover>

            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="destructive" size="sm" disabled={loading}>
                  <Trash2 className="w-4 h-4" />
                  <span className="hidden sm:inline ml-1.5">Excluir</span>
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>
                    Excluir {selectedCount} conversa{selectedCount !== 1 ? "s" : ""}?
                  </AlertDialogTitle>
                  <AlertDialogDescription>
                    Esta ação remove as conversas e todas as mensagens. Os leads serão mantidos.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancelar</AlertDialogCancel>
                  <AlertDialogAction
                    className="bg-destructive hover:bg-destructive/90"
                    onClick={() => void handleDelete()}
                  >
                    Excluir
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>

            <div className="w-px h-6 bg-border mx-0.5 hidden sm:block shrink-0" />

            <Button variant="ghost" size="sm" onClick={onCancel} disabled={loading}>
              <X className="w-4 h-4" />
              <span className="hidden sm:inline ml-1.5">Cancelar</span>
            </Button>
          </div>
        </div>
      </div>

      <BulkScheduleCloseDialog
        open={scheduleDialogOpen}
        onOpenChange={setScheduleDialogOpen}
        conversations={selectedConversations}
        workspaceId={workspaceId}
        onJustClose={() => runClose(false)}
        onJustClosePreserveAssignees={() => runClose(true)}
        onCompleted={onComplete}
      />

      <Dialog open={assignOpen} onOpenChange={setAssignOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Atribuir membro ({leadIds.length} leads)</DialogTitle>
          </DialogHeader>
          {loadingMembers ? (
            <div className="flex justify-center py-6">
              <Loader2 className="w-5 h-5 animate-spin" />
            </div>
          ) : (
            <div className="space-y-1 max-h-64 overflow-y-auto">
              {members.map((member) => (
                <button
                  key={member.id}
                  type="button"
                  className={cn(
                    "flex w-full items-center gap-2 rounded-md px-2 py-2 text-sm hover:bg-muted/60 transition-colors",
                  )}
                  onClick={() => void handleAssignMember(member.id)}
                >
                  <Avatar className="h-7 w-7">
                    <AvatarFallback className="text-xs">
                      {memberLabel(member).charAt(0).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <span className="truncate">{memberLabel(member)}</span>
                </button>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
